import createKindeClient, {
  type KindeClient,
} from "@kinde-oss/kinde-auth-pkce-js";

let kinde: KindeClient | null = null;

/** Returns the Kinde client instance, or null if not initialized. */
export function getKinde(): KindeClient | null {
  return kinde;
}

/**
 * Fetches Kinde configuration from the server and initializes the Kinde client.
 * If KINDE_DOMAIN / KINDE_CLIENT_ID are not set on the server, auth is silently disabled
 * (useful for local development without a Kinde account).
 *
 * NOTE: `is_dangerously_use_local_storage: true` is used here so the refresh token
 * survives page reloads during development. In production, configure a Kinde custom
 * domain so a secure httpOnly cookie is used instead, and remove this flag.
 */
export async function initKinde(): Promise<void> {
  try {
    const res = await fetch("/api/config");
    if (!res.ok) {
      console.warn("[auth] Could not load Kinde config — auth disabled.");
      return;
    }
    const config = (await res.json()) as {
      kindeClientId?: string | null;
      kindeDomain?: string | null;
    };
    if (!config.kindeClientId || !config.kindeDomain) {
      console.warn("[auth] Kinde env vars not set — auth disabled.");
      return;
    }

    //@ts-expect-error wrong types
    kinde = await createKindeClient({
      client_id: config.kindeClientId,
      domain: config.kindeDomain,
      redirect_uri: globalThis.location.origin,
      // TODO: Remove this flag and set up a Kinde custom domain for production.
      is_dangerously_use_local_storage: true,
      on_redirect_callback: (
        _user: unknown,
        appState: { redirectTo?: string } | null,
      ) => {
        if (appState?.redirectTo) {
          globalThis.location.href = appState.redirectTo as string;
        }
      },
    });
  } catch (err) {
    console.warn("[auth] Kinde initialization error:", err);
  }
}

/** Returns true if a user is currently authenticated. */
export async function isAuthenticated(): Promise<boolean> {
  if (!kinde) return false;
  return await kinde.isAuthenticated();
}

/** Returns the raw access token, or null if not authenticated. */
export async function getToken(): Promise<string | null> {
  if (!kinde) return null;
  return await kinde.getToken() || null;
}

/** Redirects the user to Kinde's login page, preserving current URL as post-login redirect. */
export function login(): void {
  if (!kinde) {
    console.warn("[auth] Kinde not initialized — cannot login.");
    return;
  }
  kinde.login({ app_state: { redirectTo: globalThis.location.href } });
}

/** Logs the user out via Kinde and redirects back to the app origin. */
export function logout(): void {
  if (!kinde) return;
  kinde.logout({ post_logout_redirect_url: globalThis.location.origin });
}
